<template>
    <LockDesigner>
        <template v-slot:routerWrap
            ><router-view v-slot="{ Component, route }">
                <transition>
                    <component :is="Component" :key="route.path" @update-route="handleUpdateRoute" ref="pageRef"></component>
                </transition>
            </router-view>
            <ModalWindow />
        </template>
    </LockDesigner>
</template>

<script lang="ts">
import { useRouter, useRoute } from 'vue-router';
import LockDesigner from 'Vue/components/lockDesigner/LockDesigner.vue';
import ModalWindow from 'Vue/components/lockDesigner/components/shared/Modals/ModalWindow.vue';
import { useStore } from 'vuex';
import { ref, reactive } from 'vue';

export default {
    name: 'LockDesignerRouterWrap',
    setup() {
        const route = useRoute();
        const router = useRouter();
        const store = useStore();
        let routeAnimation = reactive({ enterActive: '', exitActive: '' });
        const pageRef = ref(null);
        // Watch for changes in selections and update query params accordingly
        store.watch(
            (state) => state.selections,
            (newSelections, oldSelections) => {
                if (JSON.stringify(newSelections) !== JSON.stringify(oldSelections)) {
                    // Exclude the 'scene7ImageUrl' field
                    const queryParams = {};

                    for (const key in newSelections) {
                        if (key === 'scene7ImageUrl') continue;

                        const selection = newSelections[key];

                        if (selection) {
                            if (typeof selection === 'object' && 'id' in selection) {
                                queryParams[key] = JSON.stringify({ id: selection.id });
                            } else {
                                queryParams[key] = selection;
                            }
                        }
                    }

                    // Update query params in the route
                    router.replace({ query: queryParams });
                }
            },
            { deep: true }
        );
        // we will call the fetch function from the child component when we have already processes our default selections
        store.watch(
            (state) => state.isLoadingDefault,
            (newIsDefault) => {
                if (!newIsDefault) {
                    pageRef.value.handleFetchPageData();
                }
            },
            { deep: true }
        );

        // Fetch initial selections
        const fetchDataFromQueryString = async () => {
            await router.isReady();

            const query = route.query;

            if (Object.keys(query).length === 0) {
                await store.dispatch('fetchDefaultSelections');
            } else {
                const selections = {};

                for (const key in query) {
                    const value = query[key];

                    try {
                        const parsedValue = JSON.parse(Array.isArray(value) ? value[0] : value);

                        if (typeof parsedValue === 'object' && 'id' in parsedValue) {
                            selections[key] = { id: parsedValue.id };
                        } else {
                            selections[key] = parsedValue;
                        }
                    } catch (error) {
                        selections[key] = value;
                    }
                }

                await store.dispatch('fetchSelection', { selections, optionChanged: 'None' });
            }
        };

        fetchDataFromQueryString(); // Call the function when the component is created
        interface updateRoute {
            route: any;
            direction?: string;
        }
        // Handle route updates
        const handleUpdateRoute = ({ route: routePath, direction }: updateRoute) => {
            if (direction === 'forward') {
                routeAnimation.enterActive = 'animate__animated animate__slideInRight';
                routeAnimation.exitActive = 'animate__animated animate__fadeOutLeft';
            } else {
                routeAnimation.enterActive = 'animate__animated animate__slideOutRight';
                routeAnimation.exitActive = 'animate__animated animate__fadeInLeft';
            }

            const query = route?.query;
            router.push({ path: `/lock-designer${routePath}`, query: { ...query } });
        };

        return {
            pageRef,
            route,
            router,
            routeAnimation,
            store,
            fetchDataFromQueryString,
            handleUpdateRoute,
        };
    },

    components: {
        LockDesigner,
        ModalWindow,
    },
};
</script>
